/* eslint-disable @typescript-eslint/no-magic-numbers */
import { Component, HostBinding, Input, OnDestroy, OnChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ApplicationService } from '@app/store/application/application.service';
import { BreedsQuery } from '@app/store/breeds/breeds.query';
import { QuoteSessionQuery } from '@app/store/session/session.query';
import { QuoteSessionService } from '@app/store/session/session.service';
import { Ages } from '@constants/ages';
import { CLICK_TYPES, CLICK_LOCATIONS, EVENTS } from '@constants/analytics';
import { ROUTES } from '@constants/routes';
import { InsuranceType, getInsuranceType } from '@models/InsuranceType';
import { Modality } from '@models/Modality';
import { Pet } from '@models/Pet';
import { Species } from '@models/Species';
import { AnalyticsService } from '@services/analytics.service';
import { LoggingService } from '@services/logging.service';
import { OrchestratorService } from '@services/orchestrator/orchestrator.service';
import { getFrequencyViewValue } from '@utils/frequency.util';
import { Observable, Subject, firstValueFrom } from 'rxjs';
import { first, map } from 'rxjs/operators';

@Component({
	selector: 'qe-pet-policy-breakdown',
	templateUrl: './pet-policy-breakdown.component.html',
	styleUrls: ['./pet-policy-breakdown.component.scss'],
})
export class PetPolicyBreakdownComponent implements OnDestroy, OnChanges {
	@Input() showItemizedInfo: boolean = true;
	@Input() showAddPetButton: boolean = true;
	@Input() showEditButtons: boolean = true;
	@Input() showFullPetInfo: boolean = true;
	@Input() showPetInfoToggle: boolean = false;
	@Input() pet!: Pet;
	@HostBinding('class') public readonly klassName: string = `pet-policy`;
	public paymentFrequency$: Observable<Modality | undefined>;
	public readonly getFrequencyViewValue = getFrequencyViewValue;
	public getInsuranceType = getInsuranceType;
	public allowRemovePet$: Observable<boolean>;
	public species: typeof Species = Species;
	public ages = new Ages().values;
	public truncatedPetName?: string = this.pet?.name;
	private readonly ngUnsubscribe$: Subject<boolean> = new Subject();

	public get breedName(): string {
		return this.getBreedById(this.pet.breedId ?? ``);
	}

	public get age(): string {
		return this.ages.get(parseFloat(this.pet.ageInYears ?? ``)) ?? ``;
	}

	public get wellnessViewValue(): string {
		return (
			this.pet.wellness?.find(
				({ rawValue }) => rawValue === this.pet.wellnessValue
			)?.viewValue ?? ``
		);
	}

	public get reimbursementViewValue(): string {
		return (
			this.pet.reimbursement?.find(
				({ rawValue }) => rawValue === this.pet.reimbursementValue
			)?.viewValue ?? ``
		);
	}

	public get annualDeductibleViewValue(): string {
		return (
			this.pet.annualDeductible?.find(
				({ rawValue }) => rawValue === this.pet.annualDeductibleValue
			)?.viewValue ?? ``
		);
	}

	public get annualReimbursementViewValue(): string {
		return (
			this.pet.annualReimbursement?.find(
				({ rawValue }) => rawValue === this.pet.annualReimbursementValue
			)?.viewValue ?? ``
		);
	}

	public get examFeesViewValue(): string {
		return this.pet.examFeeCoverage ? 'Yes' : 'Opted Out';
	}

	public get drugDentalViewValue(): string {
		return this.pet.drugDental ? 'Yes' : 'Opted Out';
	}

	constructor(
		private readonly router: Router,
		private readonly dialog: MatDialog,
		private readonly session: QuoteSessionQuery,
		private readonly sessionActions: QuoteSessionService,
		private readonly breedsQuery: BreedsQuery,
		private readonly orchestrator: OrchestratorService,
		private readonly appActions: ApplicationService,
		private readonly gtmService: AnalyticsService,
		private readonly loggingService: LoggingService
	) {
		this.paymentFrequency$ = this.session.paymentFrequency$();
		this.allowRemovePet$ = this.session
			.petsState$()
			.pipe(map((pets) => pets.length > 1));
	}

	ngOnChanges(): void {
		const name = this.pet?.name ?? '';
		this.truncatedPetName = name.length > 10
		  ? `${name.slice(0, 10)}...` : name;
	}

	public getBreedById(breedId: string): string {
		return (
			this.breedsQuery.getAll().find(({ id }) => id === breedId)?.name ?? ``
		);
	}

	public showCoveragesModal(
		ev: UIEvent,
		insuranceType: InsuranceType = InsuranceType.AccidentIllness
	): void {
		ev.preventDefault();
		import(`@shared/coverages-modal/coverages-modal.component`)
			.then((c) => {
				this.dialog.open(c.CoveragesModalComponent, { data: insuranceType });
			})
			.catch((err) => this.loggingService.error(err));
	}

	public addNewPet(): void {
		import(`@molecules/add-edit-pet/add-edit-pet.component`)
			.then((c) => {
				this.dialog.open(c.AddEditPetComponent, { data: { mode: 'add' } });
			})
			.catch((err) => this.loggingService.error(err));
	}

	public editPet(petId: string = ``): void {
		this.appActions.setActivePet(petId);
		this.gtmService.push({
			event: EVENTS.CLICK,
			click_location: CLICK_LOCATIONS.SCROLL_BAR_RIGHT,
			click_text: 'Edit Pet',
			click_type: CLICK_TYPES.BUTTON,
			click_id: `${CLICK_TYPES.BUTTON}-${CLICK_LOCATIONS.SCROLL_BAR_RIGHT}-Edit-Pet`
		});
		import(`@molecules/add-edit-pet/add-edit-pet.component`)
			.then((c) => {
				this.dialog.open(c.AddEditPetComponent, { data: { mode: 'edit' } });
			})
			.catch((err) => this.loggingService.error(err));
	}

	public editPlan(petId: string = ``): void {
		this.appActions.setActivePet(petId);
		this.router.navigate([ROUTES.SELECT_A_POLICY]);
		setTimeout(() => {
			scrollTo({
				top: 0,
				left: 0,
				behavior: `smooth`,
			});
		});
		const petIndex = this.session.getPetIndex(petId);
		this.gtmService.push({
			event: `Edited Plan (Pet ${petIndex})`,
			petIndex,
			queryString: `?editplan=${petIndex}`,
		});
	}

	public removePet(petId: string = ``): void {
		if (!!petId) {
			this.gtmService.push({
				event: EVENTS.CLICK,
				click_location: CLICK_LOCATIONS.SCROLL_BAR_RIGHT,
				click_text: 'Remove Pet',
				click_type: CLICK_TYPES.BUTTON,
				click_id: `${CLICK_TYPES.BUTTON}-${CLICK_LOCATIONS.SCROLL_BAR_RIGHT}-Remove-Pet`
			});
			import(`@shared/remove-pet-modal/remove-pet-modal.component`)
				.then((c) =>
					this.dialog.open(c.RemovePetModalComponent, { data: { petId }, width: '440px' })
				)
				.then((dialogRef) => {
					dialogRef
						.afterClosed()
						.pipe(first())
						.subscribe((removePetId) => {
							if (!removePetId) return;
							this.gtmService.push({
								event: 'reset_pet_array_ga4',
								pets: undefined,
							});
							this.appActions.setLoading(true);
							const quoteSessionId = this.session.getQuoteId();
							if (!!removePetId && !!quoteSessionId) {
								this.orchestrator
									.removePet(removePetId)
									.pipe(first())
									.subscribe({
										next: async (quoteSession) => {
											this.sessionActions.update(quoteSession);
											this.appActions.setLastPetActive();
											this.appActions.setLoading(false);
											const petsOnQuote = await firstValueFrom(
												this.session.petsState$()
											);
											this.gtmService.push({
												event: 'recalculate_pet_array_ga4',
												numberOfPets: petsOnQuote.length,
												pets: this.gtmService.calculatePets(petsOnQuote),
											});
											this.gtmService.push({
												event: 'funnel_event',
												step: 'Pet Removed'
											});
										},
										error: (err) => {
											this.appActions.setLoading(false);
											this.loggingService.error(err);
										},
									});
							}
						});
				})
				.catch((err) => this.loggingService.error(err));
		}
	}

	ngOnDestroy(): void {
		this.ngUnsubscribe$.next(true);
		this.ngUnsubscribe$.unsubscribe();
	}
}
